import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { StudentResult } from '~/components/results/resultCard/mockData';

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

export const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export class CustomError extends Error {
  constructor(
    public message: string,
    public statusCode: number = 500,
  ) {
    super(message);
    this.name = 'CustomError';
    Object.setPrototypeOf(this, CustomError.prototype);
  }
}

export const generatePDF = async (result: StudentResult) => {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const bricolageGrotesqueBold = await fetch(
    '/static/fonts/BricolageGrotesque.ttf',
  ).then((res) => res.arrayBuffer());

  const dmSansRegular = await fetch('/static/fonts/DMSans.ttf').then((res) =>
    res.arrayBuffer(),
  );

  pdf.addFileToVFS(
    'BricolageGrotesque.ttf',
    btoa(String.fromCharCode(...new Uint8Array(bricolageGrotesqueBold))),
  );
  pdf.addFont('BricolageGrotesque.ttf', 'BricolageGrotesque', 'bold');

  pdf.addFileToVFS(
    'DMSans-Regular.woff2',
    btoa(String.fromCharCode(...new Uint8Array(dmSansRegular))),
  );
  pdf.addFont('DMSans-Regular.woff2', 'DMSans', 'normal');

  const headerFont = 'BricolageGrotesque';
  const bodyFont = 'DMSans';

  const margin = 15;
  const startY = 20;
  const pageWidth = pdf.internal.pageSize.width;

  const logoPath = '/static/images/logo.png';
  const logoWidth = 25;
  const logoHeight = 25;
  const headerBottomY = startY + 30;
  pdf.addImage(
    logoPath,
    'PNG',
    pageWidth - margin - logoWidth,
    headerBottomY - logoHeight,
    logoWidth,
    logoHeight,
  );

  pdf.setFont(headerFont, 'bold');
  pdf.setFontSize(16);
  pdf.text(result.fullName, margin, startY + 20);

  pdf.setFontSize(12);
  pdf.setFont(bodyFont, 'normal');
  pdf.text(`Class: ${result.className}`, margin, startY + 25);
  pdf.text(`${result.term} • ${result.session}`, margin, headerBottomY);

  pdf.setDrawColor(36, 36, 36);
  pdf.line(margin, headerBottomY + 5, pageWidth - margin, headerBottomY + 5);

  autoTable(pdf, {
    startY: headerBottomY + 10,
    head: [
      [
        'Subject',
        'CA (20)',
        'Midterm (20)',
        'Exam (60)',
        'Total (100)',
        'Grade',
        'Remark',
      ],
    ],
    headStyles: {
      fillColor: [245, 102, 6],
      textColor: 255,
      fontStyle: 'bold',
      font: headerFont,
    },
    bodyStyles: {
      font: bodyFont,
    },
    body: result.subjects.map((subject) => [
      subject.name,
      subject.cumulativeAssessment,
      subject.midterm,
      subject.examination,
      subject.totalScore,
      subject.grade,
      subject.remark,
    ]),
  });

  const finalY = (pdf as unknown as { lastAutoTable: { finalY: number } })
    .lastAutoTable.finalY;

  pdf.setDrawColor(36, 36, 36);
  pdf.line(margin, finalY + 2, pageWidth - margin, finalY + 2);

  pdf.setFontSize(12);
  pdf.setFont(headerFont, 'bold');
  pdf.text('Performance Summary:', margin, finalY + 10);
  pdf.setFont(bodyFont, 'normal');
  pdf.text(`Overall Grade: ${result.overallGrade}`, margin, finalY + 16);
  pdf.text(`Class Position: ${result.position}`, margin, finalY + 22);

  pdf.setFont(headerFont, 'bold');
  pdf.text("Teacher's Feedback:", margin, finalY + 32);
  pdf.setFont(bodyFont, 'normal');
  const splitFeedback = pdf.splitTextToSize(result.teacherFeedback, 180);
  pdf.text(splitFeedback, margin, finalY + 38);
  pdf.text(
    `- ${result.teacherName}`,
    pageWidth - margin,
    finalY + 38 + splitFeedback.length * 5,
    {
      align: 'right',
    },
  );

  pdf.setDrawColor(36, 36, 36);
  pdf.line(
    margin,
    finalY + 42 + splitFeedback.length * 5,
    pageWidth - margin,
    finalY + 42 + splitFeedback.length * 5,
  );

  pdf.setFontSize(10);
  pdf.setTextColor(100);
  pdf.setFont(bodyFont, 'normal');
  pdf.text(
    `Student ID: ${result.id}`,
    margin,
    finalY + 48 + splitFeedback.length * 5,
  );
  pdf.text(
    'Logos Royal Academy',
    pageWidth - margin,
    finalY + 48 + splitFeedback.length * 5,
    {
      align: 'right',
    },
  );

  return pdf;
};
